import React, { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { PartialDeep } from 'type-fest';
import {
  ATMContainer,
  ATMButton,
  ATMGrid,
  ATMHeader,
} from 'shared-it-appmod-ui';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import { FlightCategory } from 'src/constants';
import { useRequestContext } from 'src/contexts/request.context';
import history from 'src/history';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import { getUser } from 'src/libraries/auth.library';
import {
  getRequestStatus,
  getRequestUrl,
} from 'src/selectors/request.selector';
import { actionTypes } from 'src/ducks/request.duck';
import Moment from 'src/libraries/moment.library';
import FlightRequestForm from '../flight-request-form/flight-request-form.component';
import FlightRequestReview from '../flight-request-review/flight-request-review.component';
import styles from './flight-request-add.module.scss';

const FlightRequestAdd: React.FC = () => {
  const { state, actions } = useRequestContext();
  const [isPast, setIsPast] = useState(false);
  const { preview } = state;

  const user = getUser();

  const [data, setData] = useState<PartialDeep<IRequest>>({
    request: {
      requestorNo: user?.emp_no,
    },
  });
  const [review, setReview] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!preview) {
      actions.setRequestData();
    }
  }, [preview, actions]);

  const handleClick = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    throttle(async () => {
      if (data && data.request) {
        const formData = {
          ...data,
          request: {
            ...data.request,
            flightDate: Moment(data.request?.flightDate as Date).format(
              'YYYY/MM/DD'
            ),
            flightEndDate: data.request?.flightEndDate
              ? Moment.tz(
                  Moment(data.request.flightEndDate as Date).format('YYYY-MM-DD'),
                  'YYYY-MM-DD',
                  'America/Los_Angeles'
                ).utc().format('YYYY-MM-DDTHH:mm:ss')
              : null,
          },
        };

        let result;

        switch (formData.request.flightFlag) {
          case FlightCategory.PatrolPassenger: {
            result = await actions.createPatrolPassengerPOST(formData);
            break;
          }

          // case FlightCategory.ContractorPatrol: {
          //   result = await actions.createPatrolPassengerPOST(
          //     formData
          //   );
          //   break;
          // }

          case FlightCategory.ConstructionWork: {
            result = await actions.createConstructionPOST(formData);
            break;
          }

          case FlightCategory.ContractorConstruction: {
            result = await actions.createConstructionContractorPOST(formData);
            break;
          }

          case FlightCategory.DroneFlightInternal: {
            result = await actions.createDroneInternalPOST(formData);

            break;
          }

          case FlightCategory.DroneFlightExternal: {
            result = await actions.createDroneExternalPOST(formData);

            break;
          }
        }

        if (result && result.payload) {
          ToastSuccess(Lang.MSG_REQUEST_CREATE_SUCCESS);

          history.replace(getRequestUrl(result.payload));
        }
      }
    }, 1000),
    [data]
  );

  const handlePreview = useCallback(
    (formData) => {
      setData(formData);
      setReview(true);
    },
    [setReview, setData]
  );

  const handleDismiss = useCallback(() => {
    setIsPast(false);
  }, [setIsPast]);

  const status = [
    actionTypes.REQUEST_CREATE_PATROL_PASSENGER_DATA,
    actionTypes.REQUEST_CREATE_PATROL_PASSENGER_CONTRACTOR_DATA,
    actionTypes.REQUEST_CREATE_CONSTRUCTION_DATA,
    actionTypes.REQUEST_CREATE_CONSTRUCTION_CONTRACTOR_DATA,
    actionTypes.REQUEST_CREATE_DRONE_INTERNAL_DATA,
    actionTypes.REQUEST_CREATE_DRONE_EXTERNAL_DATA,
  ].some((actionType) => getRequestStatus(state, actionType).fetching);

  const buttons = review ? (
    <>
      <ATMButton
        secondary
        icon="arrow left"
        content={Lang.LBL_EDIT}
        onClick={() => setReview(false)}
      />
      <ATMButton
        primary
        content={Lang.LBL_SUBMIT}
        onClick={handleSubmit}
        loading={status}
        disabled={status}
      />
    </>
  ) : (
    <>
      <ATMButton
        secondary
        content={Lang.LBL_CANCEL}
        onClick={() => {
          history.goBack();
        }}
      />
      <ATMButton
        primary
        content={Lang.LBL_REVIEW_SUBMIT}
        onClick={handleClick}
      />
    </>
  );

  return (
    <ATMContainer>
      <ATMGrid>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column verticalAlign="middle">
            <ATMHeader>{Lang.TTL_FLIGHT_REQUEST_NEW}</ATMHeader>
          </ATMGrid.Column>
          <ATMGrid.Column textAlign="right" verticalAlign="top">
            {buttons}
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>

      {review ? (
        <FlightRequestReview data={data as IRequest} />
      ) : (
        <FlightRequestForm
          formRef={formRef}
          handleSubmit={handlePreview}
          defaultValues={data as IRequest}
          isPast={isPast}
          handlePast={(value) => setIsPast(value)}
          handleDismiss={handleDismiss}
        />
      )}

      <ATMGrid columns={1} className={styles.bottom}>
        <ATMGrid.Column textAlign="center" verticalAlign="top">
          {buttons}
        </ATMGrid.Column>
      </ATMGrid>
    </ATMContainer>
  );
};

export default FlightRequestAdd;
